import { cn } from '@simplywallst/ui-core/next';
import type { NodeViewProps } from '@tiptap/react';
import { NodeViewWrapper } from '@tiptap/react';
import { Button } from '@simplywallst/ui-core/next';
import BinSolid from '@simplywallst/ui-core/icons/BinSolid';
import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';
import {
  getResponsiveImage,
  useCloudflareImage,
} from '@/hooks/useCloudflareImage';
import { useEffect, useId, useRef, useState } from 'react';

export const ImageNodeWrapper = ({
  node,
  deleteNode,
  selected,
}: NodeViewProps) => {
  const { src, alt, title } = node.attrs;

  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const imageSrc = useCloudflareImage({
    src: src,
    width: 900,
    fit: 'scale-down',
  });
  const imageDragPreviewSrc = useCloudflareImage({
    src: src,
    width: 400,
    fit: 'scale-down',
    quality: 70,
    blur: 10,
  });

  const { srcSet: imageSrcSet } = getResponsiveImage({
    src: src,
    breakpoints: [1900, 1400, 800, 950, 700, 400],
  });

  useEffect(() => {
    if (!src) return;

    // Image loaded from cache
    if (imageRef.current?.complete) {
      setLoaded(true);
      setError(false);
      return;
    }
    setLoaded(false);
    setError(false);
  }, [src]);

  const isLoading = Boolean(src && !loaded && !error);
  const previewImageId = useId();

  return (
    <NodeViewWrapper className="relative">
      <img
        data-drag-handle
        data-drag-preview={previewImageId}
        ref={imageRef}
        src={imageSrc}
        srcSet={imageSrcSet}
        alt={alt}
        title={title}
        className={cn(
          'mx-auto my-0 block overflow-hidden rounded-md  border-[3px] border-solid border-transparent',
          selected && 'border-[3px] border-solid border-accent',
          isLoading || error ? 'h-[300px] opacity-0' : 'opacity-100'
        )}
        onLoad={() => {
          setLoaded(true);
          setError(false);
        }}
        onError={() => setError(true)}
      />
      {(isLoading || error) && (
        <div
          role="presentation"
          aria-label={error ? 'Image failed to load' : 'Image loading'}
          className={cn(
            'bones absolute inset-0 z-10 grid min-h-[300px] w-full place-content-center overflow-hidden rounded-md',
            error ? '' : 'animate-pulse'
          )}
        >
          {error && <CrossSolid className="h-x10 w-auto fill-bad-strong" />}
        </div>
      )}
      {selected && !isLoading && (
        <div className="absolute -bottom-x6 left-1/2 z-10 -translate-x-1/2">
          <Button
            variant="outline"
            className="bg-surface-2 shadow-crisp hover:bg-surface-3"
            onClick={deleteNode}
            aria-label="Delete"
          >
            <BinSolid />
          </Button>
        </div>
      )}
      <img
        id={previewImageId}
        src={imageDragPreviewSrc}
        className="fixed left-[-99999px] top-[-99999px]"
      />
    </NodeViewWrapper>
  );
};
